import { API_CACHE_KEYS } from "api/cacheKeys";
import { BaseQueryOptions } from "api/types";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";
import { ListResponse } from "types/api";
import { ImageQuery } from "types/imageQueries";

export type GetImageQueriesExamplesParams = {
  label?: string;
  predictorId?: string;
  currentImageQueryId?: string;
  countValue?: string;
  pageSize?: number;
};

type UseGetImageQueriesExamplesParams = {
  params?: GetImageQueriesExamplesParams;
  options?: BaseQueryOptions<GetImageQueriesExamplesResponse>;
};

type GetImageQueriesExamplesResponse = ListResponse<ImageQuery>;

const fetchImageQueriesExamples = async (
  params: GetImageQueriesExamplesParams
): Promise<GetImageQueriesExamplesResponse> => {
  if (!params.predictorId) {
    throw new Error("predictorId is required to fetch examples");
  }
  const url = buildImageQueriesExamplesUrl(params);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch examples: ${response.statusText}`);
  }
  return response.json();
};

// Helper function to build URL with parameters
export function buildImageQueriesExamplesUrl(params?: GetImageQueriesExamplesParams): string {
  const url = `${JANZU_URL}reef-api/image-queries/examples`;
  const searchParams = new URLSearchParams();

  if (params?.label !== undefined) {
    searchParams.append("label", params.label);
  }

  if (params?.predictorId !== undefined) {
    searchParams.append("predictor_id", params.predictorId);
  }

  if (params?.currentImageQueryId !== undefined) {
    searchParams.append("current_image_query_id", params.currentImageQueryId);
  }

  if (params?.pageSize !== undefined) {
    searchParams.append("page_size", params.pageSize.toString());
  }

  if (params?.countValue !== undefined) {
    searchParams.append("count_value", params.countValue);
  }

  return `${url}?${searchParams.toString()}`;
}

function useGetImageQueriesExamples({ params, options }: UseGetImageQueriesExamplesParams = {}) {
  return useCustomQuery<GetImageQueriesExamplesResponse>(
    API_CACHE_KEYS.imageQueries.examples(params),
    buildImageQueriesExamplesUrl(params),
    {
      ...options,
      enabled: (options?.enabled ?? true) && !!params?.predictorId,
    }
  );
}

type UseImageQueriesExamplesParams = {
  getOptions?: {
    params?: GetImageQueriesExamplesParams;
    options?: BaseQueryOptions<GetImageQueriesExamplesResponse>;
  };
};

export function useImageQueriesExamples({ getOptions }: UseImageQueriesExamplesParams = {}) {
  const imageQueriesExamples = useGetImageQueriesExamples({
    params: getOptions?.params,
    options: getOptions?.options,
  });

  return {
    // Get operations
    imageQueriesExamples: imageQueriesExamples.data,
    isLoadingImageQueriesExamples: imageQueriesExamples.isFetching,
    imageQueriesExamplesError: imageQueriesExamples.error,
    refetchImageQueriesExamples: imageQueriesExamples.refetch,
    fetchImageQueriesExamples,
  };
}
